import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { SignOutUser, userStateListener, db } from "./firebase";
import { doc, getDoc, DocumentData } from "firebase/firestore";
import { User as FirebaseUser } from "firebase/auth";

// 1. Define the shape of the AuthContext
interface AuthContextType {
  currentUser: FirebaseUser | null;
  setCurrentUser: React.Dispatch<React.SetStateAction<FirebaseUser | null>>;
  signOut: () => void;
  isAdmin: boolean;
  loading: boolean;
}

// 2. Create the AuthContext with default values
export const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  setCurrentUser: () => {},
  signOut: () => {},
  isAdmin: false,
  loading: true,
});

// 3. Define the props for AuthProvider
interface AuthProviderProps {
  children: ReactNode;
}

// 4. Create the AuthProvider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  const navigate = useNavigate();

  useEffect(() => {
    // userStateListener is presumably your wrapper around onAuthStateChanged
    const unsubscribe = userStateListener(async (user: FirebaseUser | null) => {
      setLoading(true);
      if (user) {
        setCurrentUser(user);
        try {
          // Check Firestore to see if this user is admin
          const docRef = doc(db, "users", user.uid);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = docSnap.data() as DocumentData;
            setIsAdmin(data.isAdmin === true);
          } else {
            // No doc => assume non-admin
            setIsAdmin(false);
          }
        } catch (error) {
          console.error("Error fetching user doc:", error);
          setIsAdmin(false);
        }
      } else {
        // No logged-in user
        setCurrentUser(null);
        setIsAdmin(false);
      }
      setLoading(false);
    });

    // Clean up the listener on unmount
    return unsubscribe;
  }, []);

  // Sign out user and redirect to homepage
  const signOut = () => {
    SignOutUser();
    setCurrentUser(null);
    setIsAdmin(false); // Make sure admin is reset
    navigate("/");
  };

  const value: AuthContextType = {
    currentUser,
    setCurrentUser,
    signOut,
    isAdmin,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// 5. Create a custom hook to use the AuthContext
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
